import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { type AddressType, BodyText, theme } from '@sg/garnish';

export const BagDisclaimers = (props: BagDisclaimersProps) => {
  const { orderChannel, palette } = props;

  const { formatMessage } = useIntl();

  return (
    <View style={styles[palette]}>
      <Disclaimer
        palette={palette}
        title={formatMessage(messages.allergensTitle)}
        message={formatMessage(messages.allergensMessage)}
      />

      {orderChannel === 'delivery' ? (
        <Disclaimer
          palette={palette}
          title={formatMessage(messages.deliveryTitle)}
          message={formatMessage(messages.deliveryMessage)}
        />
      ) : null}

      <Disclaimer
        palette={palette}
        title={formatMessage(messages.cancellationTitle)}
        message={formatMessage(messages.cancellationMessage)}
      />
    </View>
  );
};

const Disclaimer = (props: DisclaimerProps) => {
  const { palette, title, message } = props;

  return (
    <View style={styles.disclaimer}>
      <BodyText bold sizeMatch={['14']} style={palette === 'light' ? styles.title : undefined}>
        {title}
      </BodyText>

      <BodyText sizeMatch={['12']} style={palette === 'light' ? styles.description : undefined}>
        {message}
      </BodyText>
    </View>
  );
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  allergensTitle: {
    defaultMessage: 'Allergens Disclaimer',
    description: 'Bag | Disclaimers | Allergens Title',
  },
  allergensMessage: {
    defaultMessage:
      'At sweetgreen we use all major allergens in our kitchens, so we cannot guarantee that our food is completely free of any allergen. If you have a severe allergy, we recommend not ordering from our restaurant.',
    description: 'Bag | Disclaimers | Allergens Message',
  },
  deliveryTitle: {
    defaultMessage: 'Delivery Provider',
    description: 'Bag | Disclaimers | Delivery Title',
  },
  deliveryMessage: {
    defaultMessage:
      'You authorize sweetgreen to share your contact and location data with our delivery service provider.',
    description: 'Bag | Disclaimers | Delivery Message',
  },
  cancellationTitle: {
    defaultMessage: 'Cancellation Policy',
    description: 'Bag | Disclaimers | Cancellation Title',
  },
  cancellationMessage: {
    defaultMessage: 'No changes to the order can be made after placing your order.',
    description: 'Bag | Disclaimers | Cancellation Message',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  light: {
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
    borderRadius: theme.radius.large,
    padding: theme.spacing['4'],
    paddingTop: theme.spacing['6'],
    gap: theme.spacing['4'],
  },
  dark: {
    backgroundColor: theme.colors.LINEN,
    paddingTop: theme.spacing['8'],
    padding: theme.spacing['6'],
    gap: theme.spacing['4'],
  },
  disclaimer: {
    gap: theme.spacing['2'],
  },
  title: {
    color: theme.colors.DARK_KALE,
    paddingBottom: theme.spacing['4'],
    textAlign: 'center',
  },
  description: {
    color: theme.colors.CHARCOAL,
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type BagDisclaimersProps = {
  palette: 'light' | 'dark';
  orderChannel: AddressType;
};

type DisclaimerProps = {
  palette: 'light' | 'dark';
  title: string;
  message: string;
};
